import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardMedia from '@material-ui/core/CardMedia';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CardActions from '@material-ui/core/CardActions';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import CheckIcon from '@material-ui/icons/Check';
import bsaVideo from '../static/video/bsa.mp4';
import reuseVideo from '../static/video/reuse.mp4';

const styles = theme => ({
    root: {
        width: '100%',
        maxWidth: 1200,
        margin: '0 auto',
        padding: `0 ${theme.spacing.unit * 2}px`,
    },
    heroUnit: {
        backgroundColor: theme.palette.background.paper,
    },
    heroContent: {
        maxWidth: 600,
        margin: '0 auto',
        padding: `${theme.spacing.unit * 8}px 0 ${theme.spacing.unit * 6}px`,
    },
    layout: {
        width: 'auto',
        marginLeft: theme.spacing.unit * 3,
        marginRight: theme.spacing.unit * 3,
        [theme.breakpoints.up(1100 + theme.spacing.unit * 3 * 2)]: {
            width: 1100,
            marginLeft: 'auto',
            marginRight: 'auto',
        },
    },
    features: {
        padding: `${theme.spacing.unit * 8}px 0`,
    },
    highlights: {
        backgroundColor: theme.palette.background.paper,
    },
    card: {
        display: 'flex',
        verticalAlign: 'top',
    },
    cardContent: {
        flex: '1 1 auto',
    },
    video: {
        maxHeight: '70vh',
    },
    exampleText: {
        marginLeft: 'auto',
    },
    expand: {
        transform: 'rotate(0deg)',
        transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.shortest,
        }),
        margin: 'auto',
        [theme.breakpoints.up('sm')]: {
            marginRight: -8,
        },
    },
    expandOpen: {
        transform: 'rotate(180deg)',
    },
    buttonsContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: theme.spacing.unit * 3,
    },
    button: {
        margin: theme.spacing.unit,
        minWidth: '200px',
    },
    section: {
        marginTop: theme.spacing.unit * 3,
    },
    heroCard: {
        marginTop: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 3,
        borderRadius: theme.spacing.unit * 2,
        background: 'linear-gradient(135deg, #eef7ff 0%, #f9fcff 55%, #f1f4ff 100%)',
        boxShadow: '0 20px 40px rgba(17, 38, 146, 0.12)',
    },
    datasetHeroContent: {
        padding: theme.spacing.unit * 4,
    },
    lead: {
        minWidth: 760,
    },
    sectionIntro: {
        maxWidth: 720,
    },
    apiLink: {
        color: theme.palette.primary.main,
        fontWeight: 500,
    },
});

// Define-XML Editor features and highlights
const defineXmlFeatures = [
    {
        title: 'Functional',
        description:
        'The editor integrates the most useful Excel functionality and features developed specifically for Define-XML development tasks.',
    },
    {
        title: 'User-Friendly',
        description:
        'No need to know complex Define-XML structures. Focus on editing dataset metadata, while the editor will take care of all the technical details.',
    },
    {
        title: 'Free',
        description:
        'Built using Electron, React, and Node.js, the editor is a free and open-source application. The source code is available under the GNU AGPL v3 license.',
    },
];

const defineXmlHighlights = [
    {
        title: 'Built-in Editors',
        description:
        'Build-in editors play a key role in the development process of the Define-XML metadata. Editors allow to edit logically grouped metadata in an interactive way. Each of the editors is developed specifically to address individual characteristics of a metadata attribute it is designed for.',
        media: bsaVideo,
        exampleText: 'Built-in Editor Example',
    },
    {
        title: 'Reuse existing metadata',
        description:
        'The editor allows to create a Define-XML document from scratch. Still in most situations studies share a lot of information, because they are based on the same CDISC standard. So it is much easier to copy existing metadata from other datasets or Define-XML documents and then update them with details specific to your study and dataset. Copy variables from another datasets in the same study or select a Define-XML document from another study and copy from it. Load CDISC Controlled Terminology into the editor and use it to quickly create your study codelists.',
        media: reuseVideo,
        exampleText: 'Metadata Reuse Example',
    },
];

const supportedFormats = [
    'Dataset-JSON v1.1',
    'NDJSON representation of Dataset-JSON 1.1',
    'Compressed Dataset-JSON 1.1',
    'XPORT v5',
    'SAS7BDAT',
    'SPSS SAV, ZSAV, POR',
    'Stata DTA',
];

const featureColumns = [
    [
        'Reading large size datasets',
        'Dataset compare',
        'Converter between formats',
        'Filtering with value autocomplete',
        'Sorting and fast row or column navigation',
    ],
    [
        'Metadata inspection',
        'API access',
        'UI for CDISC CORE',
        'Automatic updates and automated testing',
        'Multiplatform: Windows, Linux, MacOS',
    ],
];

function CheckList({ items }) {
    return (
        <List dense>
            {items.map(item => (
                <ListItem key={item}>
                    <ListItemIcon>
                        <CheckIcon />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                </ListItem>
            ))}
        </List>
    );
}

CheckList.propTypes = {
    items: PropTypes.arrayOf(PropTypes.string).isRequired,
};

function About(props) {
    const { classes } = props;
    const [expanded, setExpanded] = useState([false, false]);
    const [selectedApp, setSelectedApp] = useState('dataset-viewer');

    const handleExpandClick = (index) => {
        const newExpanded = [...expanded];
        newExpanded[index] = !expanded[index];
        setExpanded(newExpanded);
    };

    return (
        <React.Fragment>
            <div className={classes.buttonsContainer}>
                <Button
                    variant={selectedApp === 'define-xml' ? 'contained' : 'outlined'}
                    color="primary"
                    className={classes.button}
                    onClick={() => setSelectedApp('define-xml')}
                >
                    Visual Define-XML Editor
                </Button>
                <Button
                    variant={selectedApp === 'dataset-viewer' ? 'contained' : 'outlined'}
                    color="primary"
                    className={classes.button}
                    onClick={() => setSelectedApp('dataset-viewer')}
                >
                    VDE Dataset Viewer
                </Button>
            </div>

            {selectedApp === 'define-xml' ? (
                <div className={classes.root}>
                    {/* Hero unit */}
                    <div className={classes.heroUnit}>
                        <div className={classes.heroContent}>
                            <Typography variant='display3' align='center' color='textPrimary' gutterBottom>
                                Visual Define-XML Editor
                            </Typography>
                            <Typography variant='title' align='center' color='textSecondary' paragraph>
                                A cross-platform desktop application which allows to edit and review Define-XML files in a convenient and efficient way. It fully supports CDISC Define-XML v2.0 and ARM 1.0 standards.
                            </Typography>
                        </div>
                    </div>
                    {/* End hero unit */}

                    <div className={classNames(classes.layout, classes.features)}>
                        <Grid container spacing={40}>
                            {defineXmlFeatures.map(feature => (
                                <Grid item key={feature.title} xs={12} md={4}>
                                    <Card className={classes.card}>
                                        <div className={classes.cardContent}>
                                            <CardContent>
                                                <Typography variant='headline'>{feature.title}</Typography>
                                                <Typography variant='subheading' paragraph>
                                                    {feature.description}
                                                </Typography>
                                            </CardContent>
                                        </div>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </div>
                    <div className={classes.highlights}>
                        <Grid container spacing={40} className={classes.layout}>
                            {defineXmlHighlights.map((highlight, index) => (
                                <Grid item key={highlight.title} xs={12}>
                                    <Card className={classes.card}>
                                        <div className={classes.cardContent}>
                                            <CardContent>
                                                <Typography variant='headline'>{highlight.title}</Typography>
                                                <Typography variant='subheading' paragraph>
                                                    {highlight.description}
                                                </Typography>
                                            </CardContent>
                                            <CardActions className={classes.actions} onClick={() => handleExpandClick(index)}>
                                                <Typography variant='subheading' className={classes.exampleText}>
                                                    {highlight.exampleText}
                                                </Typography>
                                                <IconButton
                                                    className={classNames(classes.expand, {
                                                        [classes.expandOpen]: expanded[index],
                                                    })}
                                                    aria-expanded={expanded[index]}
                                                    aria-label="See example"
                                                >
                                                    <ExpandMoreIcon />
                                                </IconButton>
                                            </CardActions>
                                            <Collapse in={expanded[index]} timeout="auto" unmountOnExit>
                                                <CardContent>
                                                    <CardMedia
                                                        controls
                                                        className={classes.video}
                                                        image={highlight.media}
                                                        component='video'
                                                        title='Description editor'
                                                    />
                                                </CardContent>
                                            </Collapse>
                                        </div>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </div>
                </div>
            ) : (
                <div className={classes.root}>
                    <Typography variant="h4" gutterBottom>
                        VDE Dataset Viewer
                    </Typography>

                    <Card className={classes.heroCard}>
                        <CardContent className={classes.datasetHeroContent}>
                            <Typography variant="h5" gutterBottom>
                                Explore clinical datasets with less friction
                            </Typography>
                            <Typography variant="body1" paragraph className={classes.lead} inline>
                                VDE Dataset Viewer is a desktop application for opening, inspecting, and navigating
                                datasets across common clinical data exchange formats.
                            </Typography>
                            <Typography variant="body1" className={classes.lead} inline>
                                Focuses on large dataset handling, side-by-side comparison, format conversion,
                                and CDISC-oriented review workflows in a cross-platform interface.
                            </Typography>
                        </CardContent>
                    </Card>

                    <div className={classes.section}>
                        <Typography variant="h5" gutterBottom>
                            Supported Formats
                        </Typography>
                        <Card className={classes.card}>
                            <CardContent>
                                <CheckList items={supportedFormats} />
                            </CardContent>
                        </Card>
                    </div>

                    <div className={classes.section}>
                        <Typography variant="h5" gutterBottom>
                            Features
                        </Typography>
                        <Card className={classes.card}>
                            <CardContent>
                                <Grid container spacing={16}>
                                    <Grid item xs={12} sm={6}>
                                        <CheckList items={featureColumns[0]} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <CheckList items={featureColumns[1]} />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </div>

                    <div className={classes.section}>
                        <Typography variant="h5" gutterBottom>
                            API Specification
                        </Typography>
                        <Typography variant="body1" paragraph>
                            <a className={classes.apiLink}
                               href="https://github.com/cdisc-org/DataExchange-DatasetJson-API"
                               target="_blank"
                               rel="noopener noreferrer">
                                DataExchange-DatasetJson-API
                            </a>
                        </Typography>
                    </div>

                    <div className={classes.section}>
                        <Typography variant="body1">
                            This project is licensed under the MIT License.
                        </Typography>
                    </div>
                </div>
            )}
        </React.Fragment>
    );
}

About.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(About);
